import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const FONTS = [
  { id: 'Segoe UI', label: 'Segoe UI (Default)' },
  { id: 'Arial', label: 'Arial' },
  { id: 'Georgia', label: 'Georgia (Serif)' },
  { id: 'Helvetica', label: 'Helvetica' },
  { id: 'Times New Roman', label: 'Times New Roman' },
  { id: 'Courier New', label: 'Courier New (Monospace)' },
];

const LAYOUTS = [
  { id: 'classic', label: 'Classic', description: 'Traditional layout with header on top' },
  { id: 'modern', label: 'Modern', description: 'Clean minimal design with accent sidebar' },
  { id: 'compact', label: 'Compact', description: 'Space-efficient layout for shorter documents' },
];

const COLOR_PRESETS = [
  { name: 'Blue', primary: '#2563eb', secondary: '#64748b' },
  { name: 'Green', primary: '#16a34a', secondary: '#6b7280' },
  { name: 'Purple', primary: '#7c3aed', secondary: '#64748b' },
  { name: 'Red', primary: '#dc2626', secondary: '#6b7280' },
  { name: 'Orange', primary: '#ea580c', secondary: '#78716c' },
  { name: 'Teal', primary: '#0d9488', secondary: '#64748b' },
  { name: 'Navy', primary: '#1e3a5f', secondary: '#475569' },
  { name: 'Dark', primary: '#1f2937', secondary: '#6b7280' },
];

const InvoiceTemplateCustomization = () => {
  const [activeDoc, setActiveDoc] = useState('invoice');
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchOrg(); }, []);

  const fetchOrg = async () => {
    setLoading(true);
    try {
      const { data } = await supabase?.from('organizations')?.select('*')?.limit(1)?.single();
      if (data) {
        setOrg(data);
        setForm(data);
      }
    } catch (err) {
      console.error('Fetch org error:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      if (org?.id) {
        const { error: err } = await supabase?.from('organizations')?.update({
          ...form,
          updated_at: new Date()?.toISOString()
        })?.eq('id', org?.id);
        if (err) throw err;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save template settings.');
    } finally {
      setSaving(false);
    }
  };

  const docPrefix = activeDoc;
  const primaryColor = form?.[`${docPrefix}_accent_color`] || form?.primary_color || '#2563eb';
  const fontFamily = form?.[`${docPrefix}_font_family`] || 'Segoe UI';
  const layout = form?.[`${docPrefix}_layout`] || 'classic';
  const showLogo = form?.[`${docPrefix}_show_logo`] !== false;
  const companyName = form?.invoice_header_company_name || form?.name || 'Your Company';
  const companyAddress = form?.invoice_header_address || form?.address_line1 || '1234 Main St, Houston, TX';
  const companyPhone = form?.invoice_header_phone || form?.phone || '(713) 555-0100';
  const companyEmail = form?.invoice_header_email || form?.email || 'info@company.com';

  const renderPreview = () => {
    const docLabel = activeDoc === 'invoice' ? 'INVOICE' : 'QUOTE';
    const docNum = activeDoc === 'invoice' ? '#INV-0001' : '#QT-0001';

    if (layout === 'modern') {
      return (
        <div className="bg-white rounded-xl border border-border overflow-hidden" style={{ fontFamily }}>
          <div className="flex">
            <div className="w-2 self-stretch" style={{ backgroundColor: primaryColor }} />
            <div className="flex-1 p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  {showLogo && form?.logo_url && (
                    <img src={form?.logo_url} alt="Company logo" className="h-10 w-auto object-contain mb-2" />
                  )}
                  <h2 className="text-lg font-bold" style={{ color: primaryColor }}>{companyName}</h2>
                  <p className="text-xs text-gray-500">{companyAddress}</p>
                  <p className="text-xs text-gray-500">{companyPhone} · {companyEmail}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold" style={{ color: primaryColor }}>{docLabel}</div>
                  <div className="text-sm text-gray-500">{docNum}</div>
                  <div className="text-xs text-gray-400 mt-1">{new Date()?.toLocaleDateString()}</div>
                </div>
              </div>
              <div className="border-t pt-3 mt-3">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><span className="text-gray-400 uppercase tracking-wide">Bill To</span><p className="font-semibold mt-0.5">Sample Customer</p></div>
                  <div><span className="text-gray-400 uppercase tracking-wide">Due Date</span><p className="font-semibold mt-0.5">{new Date(Date.now() + 30*86400000)?.toLocaleDateString()}</p></div>
                </div>
              </div>
              <div className="mt-3">
                <div className="text-xs text-white px-2 py-1 rounded" style={{ backgroundColor: primaryColor }}>Item Description · Qty · Price · Total</div>
                <div className="text-xs text-gray-600 px-2 py-1.5 border-b">Sample Service · 1 · $500.00 · $500.00</div>
                <div className="text-xs font-bold text-right px-2 py-1" style={{ color: primaryColor }}>Total: $500.00</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (layout === 'compact') {
      return (
        <div className="bg-white rounded-xl border border-border p-4" style={{ fontFamily }}>
          <div className="flex items-center justify-between pb-2 mb-3" style={{ borderBottom: `2px solid ${primaryColor}` }}>
            <div className="flex items-center gap-2">
              {showLogo && form?.logo_url && (
                <img src={form?.logo_url} alt="Company logo" className="h-8 w-auto object-contain" />
              )}
              <div>
                <h2 className="text-sm font-bold" style={{ color: primaryColor }}>{companyName}</h2>
                <p className="text-xs text-gray-400">{companyPhone}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold" style={{ color: primaryColor }}>{docLabel} {docNum}</div>
              <div className="text-xs text-gray-400">{new Date()?.toLocaleDateString()}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs mb-3">
            <div><span className="text-gray-400">Bill To:</span><p className="font-medium">Sample Customer</p></div>
            <div><span className="text-gray-400">Due:</span><p className="font-medium">{new Date(Date.now() + 30*86400000)?.toLocaleDateString()}</p></div>
            <div><span className="text-gray-400">Amount:</span><p className="font-bold" style={{ color: primaryColor }}>$500.00</p></div>
          </div>
          <div className="text-xs">
            <div className="flex justify-between text-white px-2 py-1 rounded-sm" style={{ backgroundColor: primaryColor }}>
              <span>Description</span><span>Total</span>
            </div>
            <div className="flex justify-between px-2 py-1 text-gray-600 border-b">
              <span>Sample Service</span><span>$500.00</span>
            </div>
          </div>
        </div>
      );
    }

    // Classic layout (default)
    return (
      <div className="bg-white rounded-xl border border-border p-5" style={{ fontFamily }}>
        <div className="flex items-start justify-between pb-4 mb-4" style={{ borderBottom: `3px solid ${primaryColor}` }}>
          <div>
            {showLogo && form?.logo_url && (
              <img src={form?.logo_url} alt="Company logo" className="h-12 w-auto object-contain mb-2" />
            )}
            <h2 className="text-xl font-bold" style={{ color: primaryColor }}>{companyName}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{companyAddress}</p>
            <p className="text-xs text-gray-500">{companyPhone} · {companyEmail}</p>
          </div>
          <div className="text-right text-white px-4 py-2 rounded-lg" style={{ backgroundColor: primaryColor }}>
            <div className="text-xs uppercase tracking-wide opacity-80">{docLabel}</div>
            <div className="text-lg font-bold">{docNum}</div>
            <div className="text-xs opacity-80">{new Date()?.toLocaleDateString()}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Bill To</p>
            <p className="text-sm font-semibold">Sample Customer</p>
            <p className="text-xs text-gray-500">customer@example.com</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Due Date</p>
            <p className="text-sm font-semibold">{new Date(Date.now() + 30*86400000)?.toLocaleDateString()}</p>
          </div>
        </div>
        <div className="text-xs">
          <div className="grid grid-cols-4 text-white px-3 py-2 rounded-t-lg" style={{ backgroundColor: primaryColor }}>
            <span>Description</span><span>Qty</span><span>Price</span><span className="text-right">Total</span>
          </div>
          <div className="grid grid-cols-4 px-3 py-2 text-gray-600 border-b">
            <span>Sample Service</span><span>1</span><span>$500.00</span><span className="text-right">$500.00</span>
          </div>
          <div className="text-right px-3 py-2 font-bold" style={{ color: primaryColor }}>Total: $500.00</div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Invoice & Quote Templates - RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-6 pb-16 px-4 max-w-6xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="Palette" size={22} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Invoice & Quote Templates</h1>
              <p className="text-sm text-muted-foreground">Customize the design of your invoices and quotes</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="space-y-5">
              {/* Document Type Tabs */}
              <div className="flex gap-1 bg-muted rounded-xl p-1">
                {['invoice', 'quote']?.map(doc => (
                  <button
                    key={doc}
                    onClick={() => setActiveDoc(doc)}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all capitalize ${
                      activeDoc === doc ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {doc === 'invoice' ? '📄 Invoice' : '📋 Quote'}
                  </button>
                ))}
              </div>

              {/* Color Presets */}
              <div className="bg-card rounded-xl border border-border p-5">
                <h3 className="font-semibold text-foreground mb-3">Brand Colors</h3>
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {COLOR_PRESETS?.map(preset => (
                    <button
                      key={preset?.name}
                      onClick={() => {
                        handleChange(`${docPrefix}_accent_color`, preset?.primary);
                        handleChange('secondary_color', preset?.secondary);
                        handleChange('primary_color', preset?.primary);
                      }}
                      className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
                        form?.[`${docPrefix}_accent_color`] === preset?.primary || form?.primary_color === preset?.primary
                          ? 'border-primary' : 'border-transparent hover:border-border'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full" style={{ backgroundColor: preset?.primary }} />
                      <span className="text-xs text-muted-foreground">{preset?.name}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Primary Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form?.[`${docPrefix}_accent_color`] || form?.primary_color || '#2563eb'}
                        onChange={(e) => { handleChange(`${docPrefix}_accent_color`, e?.target?.value); handleChange('primary_color', e?.target?.value); }}
                        className="w-10 h-8 rounded border border-border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={form?.[`${docPrefix}_accent_color`] || form?.primary_color || '#2563eb'}
                        onChange={(e) => { handleChange(`${docPrefix}_accent_color`, e?.target?.value); handleChange('primary_color', e?.target?.value); }}
                        className="w-28 px-2 py-1.5 border border-border rounded text-xs bg-background text-foreground"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Font Selection */}
              <div className="bg-card rounded-xl border border-border p-5">
                <h3 className="font-semibold text-foreground mb-3">Font Family</h3>
                <div className="grid grid-cols-2 gap-2">
                  {FONTS?.map(font => (
                    <button
                      key={font?.id}
                      onClick={() => handleChange(`${docPrefix}_font_family`, font?.id)}
                      className={`px-3 py-2 rounded-lg border text-sm text-left transition-all ${
                        (form?.[`${docPrefix}_font_family`] || 'Segoe UI') === font?.id
                          ? 'border-primary bg-primary/5 text-primary' : 'border-border text-foreground hover:border-primary/40'
                      }`}
                      style={{ fontFamily: font?.id }}
                    >
                      {font?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Layout Selection */}
              <div className="bg-card rounded-xl border border-border p-5">
                <h3 className="font-semibold text-foreground mb-3">Document Layout</h3>
                <div className="space-y-2">
                  {LAYOUTS?.map(l => (
                    <button
                      key={l?.id}
                      onClick={() => handleChange(`${docPrefix}_layout`, l?.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all ${
                        (form?.[`${docPrefix}_layout`] || 'classic') === l?.id
                          ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        (form?.[`${docPrefix}_layout`] || 'classic') === l?.id ? 'border-primary' : 'border-border'
                      }`}>
                        {(form?.[`${docPrefix}_layout`] || 'classic') === l?.id && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{l?.label}</p>
                        <p className="text-xs text-muted-foreground">{l?.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Display Options */}
              <div className="bg-card rounded-xl border border-border p-5">
                <h3 className="font-semibold text-foreground mb-3">Display Options</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div
                      onClick={() => handleChange(`${docPrefix}_show_logo`, !form?.[`${docPrefix}_show_logo`] !== false)}
                      className={`w-10 h-6 rounded-full transition-colors relative ${
                        form?.[`${docPrefix}_show_logo`] !== false ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        form?.[`${docPrefix}_show_logo`] !== false ? 'translate-x-5' : 'translate-x-1'
                      }`} />
                    </div>
                    <span className="text-sm text-foreground">Show company logo</span>
                  </label>
                  {activeDoc === 'invoice' && (
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div
                        onClick={() => handleChange('invoice_show_watermark', !form?.invoice_show_watermark)}
                        className={`w-10 h-6 rounded-full transition-colors relative ${
                          form?.invoice_show_watermark ? 'bg-primary' : 'bg-muted'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          form?.invoice_show_watermark ? 'translate-x-5' : 'translate-x-1'
                        }`} />
                      </div>
                      <span className="text-sm text-foreground">Show watermark (PAID/INVOICE)</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Save */}
              <div className="flex items-center justify-between">
                <div>
                  {error && <p className="text-sm text-red-600">{error}</p>}
                  {saved && <p className="text-sm text-green-600 flex items-center gap-1"><Icon name="CheckCircle" size={14} color="currentColor" /> Template saved!</p>}
                </div>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center gap-2"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                  ) : (
                    <><Icon name="Save" size={16} color="currentColor" /> Save Template</>
                  )}
                </button>
              </div>
            </div>

            {/* Live Preview */}
            <div className="lg:sticky lg:top-20 lg:self-start">
              <div className="bg-card rounded-xl border border-border p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Eye" size={16} color="var(--color-primary)" />
                  <h3 className="font-semibold text-foreground">Live Preview</h3>
                  <span className="text-xs text-muted-foreground capitalize ml-auto">{activeDoc} · {layout} layout</span>
                </div>
                {renderPreview()}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default InvoiceTemplateCustomization;
